import { leaveViewPath } from '../leave/leaveView'
import type { IconType } from 'react-icons'
import {
  HiOutlineAcademicCap, HiOutlineBookOpen, HiOutlineBriefcase, HiOutlineBuildingLibrary,
  HiOutlineBuildingOffice2, HiOutlineCalendarDays, HiOutlineChartBar, HiOutlineChatBubbleLeftRight, HiOutlineCheckCircle,
  HiOutlineClipboardDocumentCheck, HiOutlineClipboardDocumentList, HiOutlineCog6Tooth,
  HiOutlineDocumentText, HiOutlineEnvelope, HiOutlineFolder, HiOutlineHeart, HiOutlineIdentification,
  HiOutlineKey, HiOutlineMapPin, HiOutlineMegaphone, HiOutlinePencilSquare, HiOutlineReceiptPercent,
  HiOutlineRectangleGroup, HiOutlineScissors, HiOutlineServerStack, HiOutlineShieldCheck,
  HiOutlineSparkles, HiOutlineSquares2X2, HiOutlineSwatch, HiOutlineUserCircle, HiOutlineUserGroup,
} from 'react-icons/hi2'

// One plain-language home for every Hub destination. The header menu and every
// home layout group links with these rules, so a link never moves between them.
export type HubLinkGroupId =
  | 'me' | 'apps' | 'forms' | 'company' | 'training' | 'people' | 'office' | 'manager' | 'admin'

export interface HubLinkGroup {
  id: HubLinkGroupId
  label: string
  description: string
  icon: IconType
}

export const HUB_LINK_GROUPS: readonly HubLinkGroup[] = [
  { id: 'me', label: 'My stuff', description: 'Your details, leave, bills and tasks.', icon: HiOutlineUserCircle },
  { id: 'apps', label: 'Apps', description: 'The other Duo apps you use.', icon: HiOutlineSquares2X2 },
  { id: 'forms', label: 'Forms', description: 'Get IT help, share an idea or recognise someone.', icon: HiOutlinePencilSquare },
  { id: 'company', label: 'Company', description: 'Memos, policies and meeting minutes.', icon: HiOutlineBuildingLibrary },
  { id: 'training', label: 'Training', description: 'Required and optional learning.', icon: HiOutlineAcademicCap },
  { id: 'people', label: 'People', description: 'Your team and who’s who.', icon: HiOutlineUserGroup },
  { id: 'office', label: 'Office', description: 'Rooms, coffee and in-office perks.', icon: HiOutlineBuildingOffice2 },
  { id: 'manager', label: 'Manager tools', description: 'Hiring and running your team.', icon: HiOutlineBriefcase },
  { id: 'admin', label: 'Admin', description: 'Restricted admin and setup tools.', icon: HiOutlineCog6Tooth },
]

/** A permission-filtered navigation item, as the header already builds them. */
export interface HubLink {
  path: string
  label: string
  icon?: unknown
  isReactIcon?: boolean
  external?: boolean
  visibility?: string
  categoryName?: string | null
  /** Team apps are tools; unmatched company services are company information. */
  source?: 'team' | 'service'
  /** Where the link should go when it differs from its catalogue path. */
  to?: string
}

export interface HubLinkEntry extends HubLink {
  key: string
  to: string
  group: HubLinkGroupId
  external: boolean
}

export interface HubLinkSection {
  group: HubLinkGroup
  links: HubLinkEntry[]
}

interface Rule { group: HubLinkGroupId; label: string; icon?: IconType; to?: string }

// Order within each group follows this list. Unlisted links keep their catalogue order after these.
const RULES: Array<[string, Rule]> = [
  ['/my-profile', { group: 'me', label: 'My profile', icon: HiOutlineUserCircle }],
  ['/leave', { group: 'me', label: 'Leave', icon: HiOutlineCalendarDays }],
  ['/bills', { group: 'me', label: 'Bills & receipts', icon: HiOutlineReceiptPercent }],
  ['/tasks', { group: 'me', label: 'My tasks', icon: HiOutlineCheckCircle }],
  ['/my-tasks', { group: 'me', label: 'My tasks', icon: HiOutlineCheckCircle, to: '/tasks' }],
  ['/kpi', { group: 'me', label: 'My KPIs', icon: HiOutlineChartBar }],
  ['/documents', { group: 'me', label: 'Documents', icon: HiOutlineDocumentText }],
  ['/wise-setup', { group: 'me', label: 'Wise setup' }],
  ['/it-tickets', { group: 'forms', label: 'IT tickets' }],
  ['/einstein-submission', { group: 'forms', label: 'Einstein ideas' }],
  ['/superstar', { group: 'forms', label: 'Superstar nominations' }],
  ['/marketing-suggestions', { group: 'forms', label: 'Marketing suggestions', icon: HiOutlineMegaphone }],
  ['/memo', { group: 'company', label: 'Company memos', icon: HiOutlineEnvelope }],
  ['/policies', { group: 'company', label: 'Policies', icon: HiOutlineShieldCheck }],
  ['/minutes', { group: 'company', label: 'Meeting minutes', icon: HiOutlineClipboardDocumentList }],
  ['/handbook', { group: 'company', label: 'Handbook' }],
  ['/case-studies', { group: 'company', label: 'Case studies', icon: HiOutlineBookOpen }],
  ['/security-training', { group: 'training', label: 'Security training' }],
  // The home team overview replaces the older My Team page and adds manager columns.
  ['/my-team', { group: 'people', label: 'My team', icon: HiOutlineUserGroup, to: '/?view=team' }],
  ['/org-chart', { group: 'people', label: 'Org chart', icon: HiOutlineRectangleGroup }],
  ['/directory', { group: 'people', label: 'Company directory', icon: HiOutlineIdentification }],
  ['/meeting-room', { group: 'office', label: 'Room bookings', icon: HiOutlineBuildingOffice2 }],
  ['/parra-seating-plan', { group: 'office', label: 'Parramatta seating', icon: HiOutlineMapPin }],
  ['/cafe', { group: 'office', label: 'Duo Cafe' }],
  ['/haircuts', { group: 'office', label: 'Haircuts', icon: HiOutlineScissors }],
  ['/massages', { group: 'office', label: 'Massages', icon: HiOutlineSparkles }],
  [leaveViewPath('shutdown'), { group: 'manager', label: 'Skeleton Crew', icon: HiOutlineCalendarDays }],
  ['/interviews', { group: 'manager', label: 'Interviews', icon: HiOutlineChatBubbleLeftRight }],
  ['/uniforms', { group: 'manager', label: 'Team uniforms', icon: HiOutlineSwatch }],
  ['/staff_actions_data', { group: 'manager', label: 'Staff actions data', icon: HiOutlineClipboardDocumentCheck }],
  ['/admin', { group: 'admin', label: 'Admin', icon: HiOutlineCog6Tooth }],
  ['/system-development', { group: 'admin', label: 'System Development' }],
  ['/duo-services', { group: 'admin', label: 'Duo Services', icon: HiOutlineServerStack }],
  ['/dqs', { group: 'admin', label: 'DQS documents', icon: HiOutlineFolder }],
  ['/sharepoint-access', { group: 'admin', label: 'SharePoint access', icon: HiOutlineKey }],
  ['/wellbeing-allowance', { group: 'admin', label: 'Wellbeing allowance', icon: HiOutlineHeart }],
]
const RULE_BY_PATH = new Map(RULES.map(([path, rule], order) => [path, { ...rule, order }]))

// Company service categories from Admin → Company Services.
const CATEGORY_GROUPS: Record<string, HubLinkGroupId> = {
  people: 'people', training: 'training', submissions: 'forms', forms: 'forms',
}

function isExternal(path: string) {
  return /^https?:\/\//i.test(path)
}

function localPathname(path: string) {
  if (isExternal(path)) return null
  const pathname = path.split(/[?#]/)[0].replace(/\/+$/, '').toLowerCase()
  return pathname || '/'
}

// '/leave/new' belongs with '/leave', and '/policies/3/read' with '/policies'.
function groupRuleFor(pathname: string) {
  const exact = RULE_BY_PATH.get(pathname)
  if (exact) return exact
  let match: (Rule & { order: number }) | undefined
  let length = 0
  for (const [prefix, rule] of RULE_BY_PATH) {
    if (pathname.startsWith(`${prefix}/`) && prefix.length > length) {
      match = rule
      length = prefix.length
    }
  }
  return match
}

function exactLinkRule(path: string) {
  const pathname = localPathname(path)
  if (pathname === '/leave' && new URLSearchParams(path.split('?')[1]?.split('#')[0]).get('tab') === 'shutdown') {
    return RULE_BY_PATH.get(leaveViewPath('shutdown'))
  }
  return pathname ? RULE_BY_PATH.get(pathname) : undefined
}

export function hubLinkGroupFor(link: Pick<HubLink, 'path' | 'categoryName' | 'source'>): HubLinkGroupId {
  const pathname = localPathname(link.path)
  const rule = exactLinkRule(link.path) || (pathname ? groupRuleFor(pathname) : undefined)
  if (rule) return rule.group
  const category = link.categoryName?.trim().toLowerCase()
  if (category && CATEGORY_GROUPS[category]) return CATEGORY_GROUPS[category]
  if (isExternal(link.path) || link.source === 'team') return 'apps'
  return 'company'
}

/** Group, relabel and de-duplicate links. Empty groups are left out. */
export function groupHubLinks(links: HubLink[]): HubLinkSection[] {
  const seen = new Set<string>()
  const entries: Array<HubLinkEntry & { order: number; index: number }> = []
  links.forEach((link, index) => {
    if (!link?.path) return
    const rule = exactLinkRule(link.path)
    const to = link.to || rule?.to || link.path
    const key = to.replace(/\/+$/, '').toLowerCase() || '/'
    if (seen.has(key)) return
    seen.add(key)
    // Brand images stay recognisable; generic icons move to one consistent set.
    const hasImage = !link.isReactIcon && typeof link.icon === 'string' && /^(\/|https?:\/\/)/i.test(link.icon)
    const icon = hasImage ? link.icon : rule?.icon || (link.isReactIcon ? link.icon : undefined)
    entries.push({
      ...link, key, to, index,
      label: rule?.label || link.label.trim(),
      icon, isReactIcon: hasImage ? false : Boolean(rule?.icon || link.isReactIcon),
      external: link.external ?? isExternal(to),
      group: hubLinkGroupFor(link),
      order: rule?.order ?? Number.MAX_SAFE_INTEGER,
    })
  })
  return HUB_LINK_GROUPS.flatMap(group => {
    const members = entries.filter(entry => entry.group === group.id)
      .sort((a, b) => a.order - b.order || a.index - b.index)
      .map(({ order: _order, index: _index, ...entry }) => entry)
    return members.length ? [{ group, links: members }] : []
  })
}

export function isHubLinkActive(link: Pick<HubLinkEntry, 'to' | 'external'>, pathname: string, search: string) {
  if (link.external) return false
  const [path, query = ''] = link.to.split('?')
  if (path !== pathname) return false
  const desired = new URLSearchParams(query)
  const current = new URLSearchParams(search)
  if (path === '/leave' && (desired.get('tab') === 'shutdown') !== (current.get('tab') === 'shutdown')) return false
  const wanted = desired.get('view')
  return (new URLSearchParams(search).get('view') || null) === (wanted || null)
}

export function iconIsComponent(icon: unknown): icon is IconType {
  return typeof icon === 'function' || (typeof icon === 'object' && icon !== null && '$$typeof' in icon)
}
